import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Wait for Firebase Auth to resolve (max 10s).
 * Returns current user or null.
 */
function waitForAuth() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => { unsub(); resolve(null); }, 10000);
    const unsub = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      unsub();
      resolve(user);
    });
  });
}

// Superadmin tenant impersonation — set externally by AuthContext
let _impersonatedTenantId = null;
export function setImpersonatedTenant(tenantId) { _impersonatedTenantId = tenantId; }

/**
 * Structured API error with status code and retry info.
 * Backwards-compatible: err.message works as before.
 */
export class ApiError extends Error {
  constructor(message, status, retryable = false, body = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryable = retryable;
    // Full parsed JSON body — lets callers read structured fields like
    // 'existing' on a 409 duplicate_phone response without re-parsing.
    this.body = body;
  }
}

/**
 * Authenticated API call helper.
 * Automatically attaches Firebase Auth token to requests.
 * If superadmin impersonation is active, adds X-Tenant-Id header.
 *
 * Error handling:
 * - 401: auto-retry once with refreshed token
 * - 403 tenant_blocked: dispatches event to app
 * - 429: throws retryable ApiError (rate limited)
 * - 5xx on GET: auto-retry once, then throw retryable ApiError
 * - Network errors: throws retryable ApiError
 */
export async function apiFetch(url, options = {}, _retryState = {}) {
  const { tokenRetried, serverRetried } = _retryState;

  const user = await waitForAuth();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (user) {
    const token = await user.getIdToken(tokenRetried);
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Superadmin impersonation
  if (_impersonatedTenantId) {
    headers['X-Tenant-Id'] = _impersonatedTenantId;
  }

  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  } catch (networkErr) {
    try {
      window.dispatchEvent(new CustomEvent('api-server-error', { detail: { status: 0, url } }));
    } catch (_) { /* SSR/no-window — ignore */ }
    throw new ApiError(
      'Немає з\'єднання з сервером',
      0,
      true,
    );
  }

  if (res.ok) return res.json();

  const err = await res.json().catch(() => ({ error: res.statusText }));
  const msg = err.error || `API error ${res.status}`;

  // 401 Unauthorized — retry once with force-refreshed token
  if (res.status === 401 && !tokenRetried && user) {
    return apiFetch(url, options, { ..._retryState, tokenRetried: true });
  }

  // Tenant blocked/expired → notify app
  if (res.status === 403 && err.error === 'tenant_blocked') {
    window.dispatchEvent(new CustomEvent('tenant-blocked', { detail: err.status }));
  }

  // 429 Rate limited — dispatch event so UI can show a friendly toast
  // with the Retry-After countdown (in seconds). Caller still gets the
  // thrown ApiError so existing error paths continue to work.
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After')) || 60;
    try {
      window.dispatchEvent(new CustomEvent('api-rate-limited', { detail: { retryAfter } }));
    } catch (_) { /* SSR/no-window — ignore */ }
    throw new ApiError(msg, 429, true, err);
  }

  // 5xx Server error — retry once for GET requests after a short backoff.
  // On exhausted retries (or non-GET), dispatch api-server-error so the app
  // can show a unified toast instead of each caller handling it.
  if (res.status >= 500) {
    const method = (options.method || 'GET').toUpperCase();
    if (method === 'GET' && !serverRetried) {
      await new Promise(r => setTimeout(r, 500));
      return apiFetch(url, options, { ..._retryState, serverRetried: true });
    }
    try {
      window.dispatchEvent(new CustomEvent('api-server-error', { detail: { status: res.status, url } }));
    } catch (_) { /* SSR/no-window — ignore */ }
    throw new ApiError(msg, res.status, true, err);
  }

  // 4xx client errors (400, 403, 404, etc.) — not retryable
  throw new ApiError(msg, res.status, false, err);
}

/**
 * Авторизоване завантаження файлу «як є» (тіло — сам файл, не JSON):
 * шаблони документів .docx (24.10). Відповідь — JSON, помилки — як у
 * apiFetch (ApiError із тілом відповіді в .body).
 */
export async function apiUpload(url, file) {
  const user = await waitForAuth();
  const headers = { 'Content-Type': 'application/octet-stream' };
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  if (_impersonatedTenantId) headers['X-Tenant-Id'] = _impersonatedTenantId;
  let res;
  try {
    res = await fetch(url, { method: 'POST', headers, body: file });
  } catch {
    throw new ApiError('Немає з\'єднання з сервером', 0, true);
  }
  if (res.ok) return res.json();
  const err = await res.json().catch(() => ({ error: res.statusText }));
  throw new ApiError(err.message || err.error || `API error ${res.status}`, res.status, res.status >= 500, err);
}

/**
 * Той самий авторизований запит, але відповідь — файл (Blob), не JSON.
 * Для вкладень, які сервер проксіює з-за свого токена (Telegram getFile):
 * <a href> чи <img src> заголовок Authorization не понесуть.
 */
export async function apiFetchBlob(url) {
  const user = await waitForAuth();
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  if (_impersonatedTenantId) headers['X-Tenant-Id'] = _impersonatedTenantId;
  const res = await fetch(url, { headers });
  if (res.ok) return res.blob();
  const err = await res.json().catch(() => ({ error: res.statusText }));
  throw new ApiError(err.error || `API error ${res.status}`, res.status, res.status >= 500, err);
}
